const VILNIUS_MONTH = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Vilnius',
  year: 'numeric',
  month: '2-digit',
});

function monthKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  const parts = Object.fromEntries(
    VILNIUS_MONTH.formatToParts(date).map(({ type, value: partValue }) => [type, partValue]),
  );
  return `${parts.year}-${parts.month}`;
}

export function buildInvoiceClassWindow(
  classes = [],
  {
    now = new Date(),
    visibleOlderMonthCount = 0,
    visibleFutureMonthCount = 0,
  } = {},
) {
  const currentMonth = monthKey(now);
  const sortedClasses = classes
    .filter(({ start_time }) => monthKey(start_time) !== null)
    .slice()
    .sort((left, right) => new Date(left.start_time) - new Date(right.start_time));

  const firstOlderOutstanding = sortedClasses.find((classItem) => (
    monthKey(classItem.start_time) < currentMonth
    && Number(classItem.amount_due) > 0
  ));
  const firstOutstandingMonth = firstOlderOutstanding
    ? monthKey(firstOlderOutstanding.start_time)
    : null;
  const hasOlderOutstanding = firstOutstandingMonth !== null;
  // All returned history is browsable; outstanding debt only sets the initial window.
  const availableOlderMonths = [...new Set(
    sortedClasses
      .map(({ start_time }) => monthKey(start_time))
      .filter((classMonth) => classMonth < currentMonth),
  )];
  const initialOlderMonthCount = hasOlderOutstanding
    ? availableOlderMonths.filter((classMonth) => classMonth >= firstOutstandingMonth).length
    : 0;
  const availableFutureMonths = [...new Set(
    sortedClasses
      .map(({ start_time }) => monthKey(start_time))
      .filter((classMonth) => classMonth > currentMonth),
  )];
  const requestedOlderMonthCount = Math.max(
    0,
    Math.trunc(Number(visibleOlderMonthCount) || 0),
  );
  const requestedFutureMonthCount = Math.max(
    0,
    Math.trunc(Number(visibleFutureMonthCount) || 0),
  );
  const appliedOlderMonthCount = Math.min(
    requestedOlderMonthCount,
    availableOlderMonths.length,
  );
  const appliedFutureMonthCount = Math.min(
    requestedFutureMonthCount,
    availableFutureMonths.length,
  );
  const visibleMonths = new Set([
    currentMonth,
    ...(appliedOlderMonthCount > 0
      ? availableOlderMonths.slice(-appliedOlderMonthCount)
      : []),
    ...availableFutureMonths.slice(0, appliedFutureMonthCount),
  ]);

  const visibleClasses = sortedClasses.filter(
    ({ start_time }) => visibleMonths.has(monthKey(start_time)),
  );

  return {
    visibleClasses,
    hasOlderOutstanding,
    firstOutstandingMonth,
    initialOlderMonthCount,
    visibleOlderMonthCount: appliedOlderMonthCount,
    availableOlderMonthCount: availableOlderMonths.length,
    visibleFutureMonthCount: appliedFutureMonthCount,
    availableFutureMonthCount: availableFutureMonths.length,
    canShowMorePast: appliedOlderMonthCount < availableOlderMonths.length,
    canShowLessPast: appliedOlderMonthCount > 0,
    canShowMoreFuture: appliedFutureMonthCount < availableFutureMonths.length,
    canShowLessFuture: appliedFutureMonthCount > 0,
  };
}

export function retainVisibleSelections(selectedClassIds = [], visibleClasses = []) {
  const visibleIds = new Set(visibleClasses.map(({ id }) => id));
  return selectedClassIds.filter((id) => visibleIds.has(id));
}
